import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useChannelStateContext, useChatContext } from "stream-chat-react";
import { UserAvatar } from "@/features/chat/components/UserAvatar";
import { Modal } from "@/shared/components/ui/Modal";

interface ReadReceiptsProps {
  messageId: string;
  messageCreatedAt?: Date | string;
}

export const ReadReceipts = ({
  messageId,
  messageCreatedAt,
}: ReadReceiptsProps) => {
  const { t } = useTranslation();
  const { read, messages } = useChannelStateContext();
  const { client } = useChatContext();
  const [modalOpen, setModalOpen] = useState(false);

  if (!messageCreatedAt || !read) return null;

  const msgTime = new Date(messageCreatedAt).getTime();

  // Only show receipts on the last message from the current user
  const lastOwnMsg = [...(messages || [])]
    .reverse()
    .find((m) => m.user?.id === client.userID);
  if (lastOwnMsg?.id !== messageId) return null;

  const readers = Object.values(read).filter((r) => {
    if (r.user?.id === client.userID) return false;
    return new Date(r.last_read).getTime() >= msgTime;
  });

  if (readers.length === 0) return null;

  return (
    <>
      <button
        onClick={() => setModalOpen(true)}
        className="flex -space-x-1 mt-0.5 justify-end pr-1 cursor-pointer hover:opacity-80 transition-opacity"
        title={t("chat.seen_by")}
      >
        {readers.slice(0, 3).map((r) => (
          <div
            key={r.user!.id}
            className="ring-2 ring-white rounded-full"
          >
            <UserAvatar
              name={r.user!.name}
              id={r.user!.id}
              image={r.user!.image as string}
              size={14}
            />
          </div>
        ))}
        {readers.length > 3 && (
          <span className="text-[10px] text-slate-400 ml-1 self-center">
            +{readers.length - 3}
          </span>
        )}
      </button>

      <Modal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        title={`${t("chat.seen_by")} (${readers.length})`}
      >
        {readers.length === 0 ? (
          <p className="text-sm text-gray-400 italic text-center py-6">
            {t("chat.no_viewers")}
          </p>
        ) : (
          <div className="overflow-x-auto -mx-4 px-4">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs text-gray-500 border-b border-gray-200">
                  <th className="pb-2 font-medium">{t("feed.viewer_name")}</th>
                  <th className="pb-2 font-medium">{t("feed.viewer_company")}</th>
                  <th className="pb-2 font-medium">{t("feed.viewer_datetime")}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {readers.map((r) => {
                  const user = r.user!;
                  const isOfficer =
                    ((user as any)?.user_type || "").toLowerCase() === "officer";
                  const company =
                    (user as any)?.company_name || (user as any)?.company || "";
                  const readAt = new Date(r.last_read);
                  const timeLabel = readAt.toLocaleString([], {
                    month: "short",
                    day: "numeric",
                    hour: "2-digit",
                    minute: "2-digit",
                  });

                  return (
                    <tr key={user.id} className="text-gray-700">
                      <td className="py-2 pr-3 flex items-center gap-2">
                        <UserAvatar
                          name={user.name}
                          id={user.id}
                          image={user.image as string}
                          size={28}
                        />
                        <span>{user.name || user.id}</span>
                      </td>
                      <td className="py-2 pr-3 text-gray-500">
                        {!isOfficer && company ? company : "—"}
                      </td>
                      <td className="py-2 text-gray-400 text-xs whitespace-nowrap">
                        {timeLabel}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Modal>
    </>
  );
};
